import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sun, ExternalLink, Loader2, AlertCircle, TrendingUp, TrendingDown, FileText, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkline } from "@/components/Sparkline";
import { Seo } from "@/components/Seo";
import { relativeTime } from "@/lib/time";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_LABELS = {
  visual: "Visual", auditory: "Auditory", kinesthetic: "Kinesthetic", reading: "Reading/Writing",
};

function scoreBar(score) {
  if (score >= 75) return { bar: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-800", label: "Strength" };
  if (score >= 55) return { bar: "bg-amber-500", pill: "bg-amber-100 text-amber-800", label: "On track" };
  return { bar: "bg-red-500", pill: "bg-red-100 text-red-800", label: "Focus" };
}

export default function PublicPortfolio() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [metaRes, profileRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/portfolio/public/${token}/meta`),
          fetch(`${BACKEND_URL}/api/portfolio/public/${token}/profile`),
        ]);
        if (!metaRes.ok) {
          setError(metaRes.status === 404 ? "This link has expired or been revoked." : "Unable to load portfolio.");
          return;
        }
        setMeta(await metaRes.json());
        if (profileRes.ok) setProfile(await profileRes.json());
      } catch {
        setError("Network error — please try again.");
      }
    })();
  }, [token]);

  const pdfUrl = `${BACKEND_URL}/api/portfolio/public/${token}/pdf`;

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF9] p-6" data-testid="public-portfolio-error">
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-3xl p-10 text-center shadow-sm">
          <AlertCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="mt-6 font-display text-2xl font-semibold">Link unavailable</h1>
          <p className="mt-2 text-stone-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF9]">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const strengths = profile?.strengths_weaknesses?.strengths || [];
  const weaknesses = profile?.strengths_weaknesses?.weaknesses || [];
  const subjects = profile?.subjects_catalog || [];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="public-portfolio-page">
      <Seo
        title={`${meta.student_name} — Homeschool Portfolio`}
        description={`Read-only Florida homeschool portfolio for ${meta.student_name}, Grade ${meta.grade}. Shared securely by ${meta.teacher_name} via HomeSchool Hub.`}
        path={`/portfolio/share/${token}`}
        noindex
      />
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-lg">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-amber-500 text-white">
              <Sun className="w-5 h-5" />
            </span>
            HomeSchool Hub
          </div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-stone-500">Read-only</div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Homeschool Portfolio</div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="portfolio-student-name">
          {meta.student_name}
        </h1>
        <div className="mt-2 text-stone-500">
          Grade {meta.grade} · Teacher: <span className="font-medium text-stone-800">{meta.teacher_name}</span>
          {profile?.learning_style && (
            <> · <span className="font-medium text-stone-800">{STYLE_LABELS[profile.learning_style] || profile.learning_style}</span> learner</>
          )}
        </div>

        <Tabs defaultValue="profile" className="mt-8">
          <TabsList className="rounded-full bg-white border border-stone-200 p-1.5" data-testid="public-tabs">
            <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-profile">
              <User className="w-3.5 h-3.5 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-portfolio">
              <FileText className="w-3.5 h-3.5 mr-2" /> Portfolio PDF
            </TabsTrigger>
          </TabsList>

          {/* Profile tab */}
          <TabsContent value="profile" className="mt-6">
            {!profile ? (
              <div className="text-stone-500">Loading profile…</div>
            ) : (
              <div className="space-y-6">
                {/* Strengths / weaknesses */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5" data-testid="public-strengths">
                    <div className="flex items-center gap-2 font-medium text-emerald-800">
                      <TrendingUp className="w-4 h-4" /> Strengths
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {strengths.length === 0 && <span className="text-sm text-emerald-800/70">No proficiencies rated yet.</span>}
                      {strengths.map((s) => (
                        <span key={s} className="rounded-full bg-white border border-emerald-200 text-emerald-800 px-3 py-1 text-xs font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-red-200 bg-red-50/70 p-5" data-testid="public-weaknesses">
                    <div className="flex items-center gap-2 font-medium text-red-800">
                      <TrendingDown className="w-4 h-4" /> Areas of focus
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {weaknesses.length === 0 && <span className="text-sm text-red-800/70">Nothing flagged.</span>}
                      {weaknesses.map((s) => (
                        <span key={s} className="rounded-full bg-white border border-red-200 text-red-800 px-3 py-1 text-xs font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Interests + last update */}
                {profile.interests && (
                  <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Interests</div>
                    <div className="mt-2 text-stone-800">{profile.interests}</div>
                  </div>
                )}

                {/* Subject proficiencies (read-only bars + sparklines) */}
                <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="font-display text-2xl font-semibold">Proficiency snapshot</h2>
                    {profile.proficiencies_updated && (
                      <div className="text-xs text-stone-500" data-testid="public-updated">
                        Last updated {relativeTime(profile.proficiencies_updated)}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-stone-500 mt-1">
                    Assessed by the teacher on a 0–100 scale. Trend line shows recent changes.
                  </p>
                  <div className="mt-6 space-y-4">
                    {subjects.map((subject) => {
                      const entry = profile.proficiencies[subject];
                      if (!entry) return null;
                      const meta = scoreBar(entry.score);
                      const points = (profile.history || {})[subject] || [];
                      const focus = (profile.grade_focus || {})[subject];
                      return (
                        <div key={subject} className="rounded-xl border border-stone-100 p-4" data-testid={`public-prof-${subject}`}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-semibold">{subject}</span>
                                <span className={`inline-flex items-center rounded-full ${meta.pill} px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-semibold`}>{meta.label}</span>
                              </div>
                              {focus && focus.length > 0 && (
                                <div className="mt-1 text-[11px] text-stone-500">Grade {profile.grade} focus: {focus.join(" · ")}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <Sparkline points={points} />
                              <div className="font-display text-xl font-bold w-10 text-right">{entry.score}</div>
                            </div>
                          </div>
                          <div className="mt-3 h-2 rounded-full bg-stone-100 overflow-hidden">
                            <div className={`h-full ${meta.bar}`} style={{ width: `${entry.score}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(profile.proficiencies).length === 0 && (
                      <div className="text-sm text-stone-500">No proficiencies recorded yet.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Portfolio PDF tab */}
          <TabsContent value="portfolio" className="mt-6">
            <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
              <iframe
                title="Student portfolio PDF"
                src={pdfUrl}
                className="w-full h-[800px] border-0"
                data-testid="portfolio-pdf-iframe"
              />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="portfolio-download-btn">
                <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                  Download PDF <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <div className="text-xs text-stone-500">
                Complies with Florida Statute 1002.41 record-keeping guidance.
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-16 text-center text-xs text-stone-400">
          Shared via HomeSchool Hub. Read-only link — may be revoked by the teacher at any time.
        </div>
      </div>
    </div>
  );
}
